import { useState, useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  SearchParams,
  SearchState,
  SearchFilters,
  SearchType,
  SearchResult
} from '../types/search';
import { Content } from '../types/content';
import { Topic } from '../types/topic';
import { SearchService } from '../services/search/searchService';
import { parseSearchParams } from '../utils/api/searchUtils';
import { SEARCH_RESULT_LIMIT, DEFAULT_SEARCH_TYPE, UI_SETTINGS } from '../constants/search';
import { env } from '../config/env';
import { useGridLayout } from '../hooks/useGridLayout';
import { calculateOptimalLimit } from './useGridLayout';
import { useMediaQuery } from './useMediaQuery';
import { FILTER_DEFINITIONS } from '../config/filter.config';

const searchService = SearchService.getInstance({
  baseURL: env.apiBaseUrl,
  timeout: 30000
});

type SearchResults = Content | Topic;

const initialState: SearchState<SearchResults> = {
  term: '',
  results: [],
  loading: false,
  error: null,
  hasMore: false,
  page: 1,
  total: 0,
  filters: {
    topics: [],
    duration: undefined,
    language: undefined,
    status: undefined,
    sortBy: undefined,
    sortType: undefined,
    broadcastStatus: []
  }
};

export function useSearch() {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(`(max-width: ${UI_SETTINGS.MOBILE_BREAKPOINT}px)`);
  const [state, setState] = useState<SearchState<SearchResults>>(() => {
    // Initialize state from URL parameters
    const urlParams = parseSearchParams(location.search);
    return {
      ...initialState,
      term: urlParams.term || '',
      filters: urlParams.filters,
      page: urlParams.page || 1
    };
  });
  const isLoadingRef = useRef(false);

  const calculateLimit = useCallback((contentType: SearchType) => {
    if (contentType === 'topics' || isMobile) return SEARCH_RESULT_LIMIT;
    return calculateOptimalLimit(
      contentType === 'shorts' ? 'shorts' : 'regular',
      window.innerWidth
    );
  }, [isMobile]);

  const calculateHasMore = useCallback((total: number, currentResults: SearchResults[], limit: number) => {
    return currentResults.length < total;
  }, []);

  const updateUrl = useCallback((params: SearchParams) => {
    const searchParams = new URLSearchParams();
    
    // Add base params
    if (params.term) searchParams.set('term', params.term);
    if (params.contentType) searchParams.set('contentType', params.contentType);
    if (params.page) searchParams.set('page', params.page.toString());
    if (params.limit) searchParams.set('limit', params.limit.toString());
    
    // Add filter params using the centralized configuration
    Object.values(FILTER_DEFINITIONS).forEach(filterDef => {
      const selectedValues = filterDef.getSelectedValues(params.filters);
      if (selectedValues.length > 0) {
        const urlValue = filterDef.type === 'single' 
          ? filterDef.serializeUrlValue(selectedValues[0])
          : filterDef.serializeUrlValue(selectedValues);
        searchParams.set(filterDef.urlParam, urlValue);
      }
    });

    navigate({
      pathname: location.pathname,
      search: searchParams.toString()
    }, { replace: true });
  }, [navigate, location.pathname]);

  const parseUrlFilters = useCallback((searchParams: URLSearchParams): SearchFilters => {
    const filters: SearchFilters = { ...initialState.filters };

    Object.values(FILTER_DEFINITIONS).forEach(filterDef => {
      const urlValue = searchParams.get(filterDef.urlParam);
      if (urlValue) {
        const parsedValue = filterDef.parseUrlValue(urlValue);
        (filters as any)[filterDef.id] = parsedValue;
      }
    });

    return filters;
  }, []);

  const executeSearch = useCallback(async (params: SearchParams, shouldAppend = false) => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const result = await searchService.search(params);
      
      setState(prev => {
        const newResults = shouldAppend
          ? [...prev.results, ...result.results]
          : result.results;

        const hasMore = calculateHasMore(result.total, newResults, params.limit || SEARCH_RESULT_LIMIT);

        return {
          ...prev,
          term: params.term || '',
          results: newResults,
          hasMore,
          page: params.page || 1,
          total: result.total,
          loading: false,
          filters: params.filters
        };
      });
    } catch (error) {
      console.error('Search error:', error);
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'An error occurred',
        loading: false
      }));
    } finally {
      isLoadingRef.current = false;
    }
  }, [calculateHasMore]);

  // Update search when URL changes
  useEffect(() => {
    const urlSearchParams = new URLSearchParams(location.search);
    const filters = parseUrlFilters(urlSearchParams);
    const term = urlSearchParams.get('term') || '';
    const contentType = (urlSearchParams.get('contentType') as SearchType) || DEFAULT_SEARCH_TYPE;
    const page = parseInt(urlSearchParams.get('page') || '1', 10);
    
    const hasActiveFilters = Object.entries(filters).some(([_, value]) => 
      Array.isArray(value) ? value.length > 0 : value !== undefined
    );
    
    if (term || contentType !== DEFAULT_SEARCH_TYPE || hasActiveFilters) {
      const limit = calculateLimit(contentType);
      executeSearch({ term, contentType, filters, page, limit });
    }
  }, [location.search, executeSearch, calculateLimit, parseUrlFilters]);

  const search = useCallback((
    term?: string,
    contentType: SearchType = DEFAULT_SEARCH_TYPE,
    filters: SearchFilters = state.filters
  ) => {
    const limit = calculateLimit(contentType);
    const params: SearchParams = {
      ...(term && { term: term.trim() }),
      contentType,
      filters,
      page: 1,
      limit
    };
    
    updateUrl(params);
    executeSearch(params, false);
  }, [state.filters, updateUrl, executeSearch, calculateLimit]);

  const loadMore = useCallback(() => {
    if (isLoadingRef.current || !state.hasMore) return;

    const urlSearchParams = new URLSearchParams(location.search);
    const contentType = (urlSearchParams.get('contentType') as SearchType) || DEFAULT_SEARCH_TYPE;
    const params: SearchParams = {
      term: state.term,
      contentType,
      filters: state.filters,
      page: state.page + 1,
      limit: SEARCH_RESULT_LIMIT
    };

    executeSearch(params, true);
  }, [
    state.term,
    state.page,
    state.hasMore,
    state.filters,
    location.search,
    executeSearch
  ]);

  const updateFilters = useCallback((newFilters: Partial<SearchFilters>) => {
    const updatedFilters = {
      ...state.filters,
      ...newFilters
    };

    const urlSearchParams = new URLSearchParams(location.search);
    const contentType = (urlSearchParams.get('contentType') as SearchType) || DEFAULT_SEARCH_TYPE;
    const limit = calculateLimit(contentType);
    const params: SearchParams = {
      ...(state.term && { term: state.term }),
      contentType,
      filters: updatedFilters,
      page: 1,
      limit
    };

    updateUrl(params);
    executeSearch(params, false);
  }, [state.term, state.filters, location.search, updateUrl, executeSearch, calculateLimit]);

  const scrollToResults = useCallback(() => {
    const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-total-height')) || 0;
    const resultsElement = document.getElementById('search-results');
    
    if (resultsElement) {
      const elementPosition = resultsElement.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerHeight;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  }, []);

  const setPage = useCallback((page: number) => {
    if (isMobile) return;

    const urlSearchParams = new URLSearchParams(location.search);
    const contentType = (urlSearchParams.get('contentType') as SearchType) || DEFAULT_SEARCH_TYPE;
    const limit = calculateLimit(contentType);
    const params: SearchParams = {
      term: state.term,
      contentType,
      filters: state.filters,
      page,
      limit
    };

    updateUrl(params);
    executeSearch(params, false).then(() => {
      setTimeout(scrollToResults, 100);
    });
  }, [
    state.term,
    state.filters,
    location.search,
    updateUrl,
    executeSearch,
    isMobile,
    calculateLimit,
    scrollToResults
  ]);

  return {
    ...state,
    search,
    loadMore,
    updateFilters,
    setPage
  };
} 